import React, { FormEvent, useState } from "react";

import AuthLayout from "../components/templates/AuthLayout";
import AuthService from "../services/AuthService";

type ForgotPasswordResponse = {
  data: null;
  status: "success" | "error";
  code: number;
  errors: string[];
};

export default function ForgotPassword() {
  const { forgotPassword } = AuthService();
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fields, setFields] = useState({ username: "", email: "" });

  const updateField = <K extends keyof typeof fields>(
    field: K,
    value: (typeof fields)[K],
  ) => {
    setFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    setIsSubmitting(true);

    try {
      const response = (await forgotPassword({
        username: fields.username,
        email: fields.email,
      })) as ForgotPasswordResponse;

      if (response?.code === 200) {
        setSuccessMessage(
          "If the account exists, a temporary password was sent.",
        );
        setFields({ username: "", email: "" });
        return;
      }

      if (response?.code >= 400 && response?.code < 600) {
        const combinedErrors = response.errors.join(" ");
        setErrorMessage(
          combinedErrors || "Unable to process forgot password request.",
        );
        return;
      }

      setErrorMessage("Unable to process forgot password request.");
    } catch (error) {
      setErrorMessage("Unable to process forgot password request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <h1 className="mb-1 text-2xl font-medium text-gray-900 dark:text-gray-100">
        Forgot password
      </h1>
      <p className="mb-7 text-sm text-gray-500 dark:text-gray-400">
        Enter your username and email to receive a temporary password.
      </p>

      {successMessage && (
        <div
          role="alert"
          className="mb-4 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-200"
        >
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div
          role="alert"
          className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300"
        >
          {errorMessage}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <label
          htmlFor="username"
          className="mb-1.5 block text-sm font-medium text-gray-900 dark:text-gray-200"
        >
          Username
        </label>
        <input
          id="username"
          name="username"
          type="text"
          autoComplete="username"
          required
          value={fields.username}
          onChange={(e) => updateField("username", e.target.value)}
          placeholder="jdoe"
          className="mb-4 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-500 dark:bg-slate-700 dark:text-gray-100 dark:placeholder-gray-500"
        />

        <label
          htmlFor="email"
          className="mb-1.5 block text-sm font-medium text-gray-900 dark:text-gray-200"
        >
          Email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={fields.email}
          onChange={(e) => updateField("email", e.target.value)}
          placeholder="name@example.com"
          className="mb-4 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-500 dark:bg-slate-700 dark:text-gray-100 dark:placeholder-gray-500"
        />

        <button
          type="submit"
          disabled={isSubmitting}
          className="mb-4 flex w-full items-center justify-center rounded-md bg-blue-700 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-blue-400 dark:bg-blue-600 dark:hover:bg-blue-500 dark:focus:ring-offset-slate-800 dark:disabled:bg-blue-900 dark:disabled:text-blue-300"
        >
          {isSubmitting ? (
            <>
              <svg
                className="mr-2 h-4 w-4 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  opacity="0.25"
                />
                <path
                  d="M4 12a8 8 0 0 1 8-8"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
              Sending...
            </>
          ) : (
            "Send email"
          )}
        </button>

        <p className="text-center text-sm text-gray-600 dark:text-gray-400">
          Remember your password?{" "}
          <a
            href="/database/index.php"
            className="font-medium text-blue-700 hover:underline dark:text-blue-400"
          >
            Sign in
          </a>
        </p>
      </form>
    </AuthLayout>
  );
}
